<template>
  <div class="dashboard-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="openEmailContactsModal" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <Topbar />
      <!-- CONTENT -->
      <div class="content">
        <!-- Loading State -->
        <div v-if="loadingGlobalStats" class="loading-state">
          <div class="spinner"></div>
          <p>Loading dashboard...</p>
        </div>

        <!-- Dashboard Content -->
        <div v-else>
          <!-- GLOBAL MONTH PICKER -->
          <div class="picker-row">
            <div class="global-month-picker">
              <button class="month-nav-btn" @click="changeMonth(-1)" aria-label="Previous month">‹</button>
              <span class="month-nav-label">{{ selectedMonthLabel }}</span>
              <button class="month-nav-btn" @click="changeMonth(1)" aria-label="Next month">›</button>
            </div>
          </div>

          <!-- STAT CARDS -->
          <div class="stats-row">
            <div class="stat-card">
              <div class="info">
                <div class="label">Total Properties</div>
                <div class="value">{{ globalStats.totalProperties || 0 }}</div>
                <div class="sub">Under management</div>
              </div>
              <div class="stat-icon purple">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
            </div>

            <div class="stat-card">
              <div class="info">
                <div class="label">Month-end Status</div>
                <div class="value">{{ monthEndReportsStats.complete || 0 }} <span style="font-size:1.1rem;color:var(--text-muted);font-weight:500;">of {{ monthEndReportsStats.total_portfolio || 0 }} complete</span></div>
                <div class="mini-progress-bar">
                  <div class="mini-progress-fill" :style="{ width: monthEndProgressPercent + '%' }"></div>
                </div>
              </div>
              <div class="stat-icon amber">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <line x1="10" y1="9" x2="8" y2="9"/>
                </svg>
              </div>
            </div>

            <div class="stat-card">
              <div class="info">
                <div class="label">Outstanding Payments</div>
                <div class="value">{{ formatCurrency(outstandingPayments.amount || 0) }}</div>
                <div class="sub">{{ outstandingPayments.count_unpaid || 0 }} unpaid of {{ outstandingPayments.total_billed || 0 }}</div>
              </div>
              <div class="stat-icon green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 7c0-5.333-8-5.333-8 0"/>
                  <path d="M10 7v14"/>
                  <path d="M6 21h12"/>
                  <path d="M6 13h10"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- COMMISSION BANNER -->
          <div class="commission-banner">
            <div class="commission-main">
              <div class="commission-label">Your Commission</div>
              <div class="commission-value">{{ formatCurrency(globalStats.totalManagementCommissionExVAT || 0) }}</div>
              <div class="commission-sub">{{ selectedMonthLabel }} · {{ globalStats.totalProperties || 0 }} properties · ex. VAT</div>
            </div>
            <div class="commission-breakdown">
              <div class="breakdown-item">
                <div class="b-label">Inc. VAT</div>
                <div class="b-value">{{ formatCurrency(globalStats.totalManagementCommission || 0) }}</div>
              </div>
              <div class="breakdown-item">
                <div class="b-label">VAT</div>
                <div class="b-value">{{ formatCurrency(globalStats.totalVAT || 0) }}</div>
              </div>
              <div class="breakdown-item">
                <div class="b-label">Owner Settlements</div>
                <div class="b-value">
                  {{ formatCurrency(globalStats.totalOwnerNetEarnings || 0) }}
                </div>
              </div>
            </div>
          </div>

          <!-- BOTTOM GRID -->
          <div class="bottom-grid">
            <!-- NEEDS ATTENTION CARD -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title">Needs Attention</div>
                  <div style="font-size:0.72rem;color:var(--text-muted);margin-top:2px;">{{ selectedMonthLabel }}</div>
                </div>
                <router-link to="/finance/month-end" class="card-action">Go to Month-end →</router-link>
              </div>
              <div class="card-body">
                <div class="report-rows">
                  <div
                    v-for="item in needsAttentionList"
                    :key="item.report_id"
                    class="report-row"
                  >
                    <div style="flex:1;min-width:0;">
                      <div class="prop">{{ item.property_name }}</div>
                      <div class="owner">Owner: {{ item.owner_name || '—' }}</div>
                    </div>
                    <div class="fee">{{ formatCurrency(item.amount || 0) }}</div>
                    <span :class="['status-pill', pillClassForAction(item.action)]">{{ pillLabelForAction(item.action) }}</span>
                  </div>
                  <div v-if="needsAttentionList.length === 0" class="empty-state-small">
                    <p>All caught up</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- PROPERTIES CARD -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title">Commission by Property</div>
                  <div style="font-size:0.72rem;color:var(--text-muted);margin-top:2px;">{{ selectedMonthLabel }} · ranked by commission</div>
                </div>
              </div>
              <div class="card-body">
                <div class="prop-list">
                  <div
                    v-for="property in topPropertiesByCommission.slice(0, 6)"
                    :key="property.id"
                    class="prop-row"
                  >
                    <div class="prop-initial">{{ getPropertyInitials(property.name) }}</div>
                    <div class="prop-info">
                      <div class="prop-name">{{ property.name }}</div>
                      <div class="prop-owner">{{ getPropertyOwner(property) }}</div>
                    </div>
                    <div style="text-align:right;">
                      <div class="prop-commission">{{ formatCurrency(property.managementCommission || 0) }}</div>
                      <div class="prop-commission-lbl">commission</div>
                    </div>
                  </div>
                  <div v-if="topPropertiesByCommission.length === 0" class="empty-state-small">
                    <p>No commission data available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';

export default {
  name: 'DashboardView',
  components: {
    Sidebar,
    Topbar
  },
  data() {
    return {
      isCollapsed: false,
      baseUrl: SETTINGS.API.baseUrl,

      // Selected month for the whole dashboard
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,

      // Properties data
      properties: [],

      // Global statistics (aggregated from all properties)
      globalStats: {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0,
        totalBookings: 0,
        totalManagementCommission: 0, // Real management fees
        totalManagementCommissionExVAT: 0, // Management fees without VAT
        totalVAT: 0, // VAT on management fees
        totalOwnerNetEarnings: 0
      },

      // Real reports statistics from backend MonthlyReport model
      monthEndReportsStats: {
        total: 0,
        draft: 0,
        sent: 0,
        ready_to_send: 0,
        pushed_to_xero: 0,
        complete: 0,
        total_portfolio: 0
      },

      // Outstanding payments (owner-side) for the selected month
      outstandingPayments: {
        amount: 0,
        count_unpaid: 0,
        total_billed: 0
      },

      // Unified action queue for the selected month (drafts + unsent + unpaid)
      needsAttention: [],

      // Month-end reports with their real statuses
      monthEndReports: [], // List of reports from backend with status for each property

      // Individual property data for calculations
      propertiesData: [],

      // Global reservations lists (all properties combined)
      globalArrivingToday: [],
      globalActiveReservations: [],

      // Selected property for reservations
      selectedPropertyForReservations: null,
      selectedPropertyData: {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      },

      // Loading states
      loadingGlobalStats: true,
      loadingPropertyReservations: true,

      // UI states
      showReservations: false
    };
  },

  computed: {
    // Global month picker label
    selectedMonthLabel() {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[this.selectedMonth - 1]} ${this.selectedYear}`;
    },

    // Needs Attention (capped for the dashboard card)
    needsAttentionList() {
      return (this.needsAttention || []).slice(0, 6);
    },

    // Top properties by commission
    topPropertiesByCommission() {
      if (!this.propertiesData || this.propertiesData.length === 0) return [];

      return [...this.propertiesData]
        .sort((a, b) => (b.managementCommission || 0) - (a.managementCommission || 0))
        .slice(0, 6);
    },

    monthEndProgressPercent() {
      const total = this.monthEndReportsStats.total_portfolio || 0;
      if (total === 0) return 0;
      const complete = this.monthEndReportsStats.complete || 0;
      return Math.round((complete / total) * 100);
    }
  },

  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },

    loadSelectedMonth() {
      try {
        const saved = localStorage.getItem('dashboardSelectedMonth');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && Number.isInteger(parsed.year) && Number.isInteger(parsed.month) && parsed.month >= 1 && parsed.month <= 12) {
            this.selectedYear = parsed.year;
            this.selectedMonth = parsed.month;
            return;
          }
        }
      } catch (e) {
        // Ignore, fall back to defaults
      }
      const now = new Date();
      this.selectedYear = now.getFullYear();
      this.selectedMonth = now.getMonth() + 1;
    },

    changeMonth(delta) {
      let year = this.selectedYear;
      let month = this.selectedMonth + delta;
      while (month > 12) { month -= 12; year += 1; }
      while (month < 1) { month += 12; year -= 1; }
      this.selectedYear = year;
      this.selectedMonth = month;
      try {
        localStorage.setItem('dashboardSelectedMonth', JSON.stringify({ year, month }));
      } catch (e) {
        // Ignore quota errors
      }
      this.fetchDashboardData();
    },

    openEmailContactsModal() {
      this.$router.push('/reservations?openContacts=true');
    },

    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },

    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },

    getPropertyInitials(name) {
      if (!name) return '??';
      const words = name.trim().split(' ').filter(w => w.length > 0);
      if (words.length === 0) return '??';
      if (words.length === 1) {
        // Un seul mot : prendre les 2 premières lettres
        return words[0].substring(0, 2).toUpperCase();
      }
      // Plusieurs mots : première lettre de chaque mot
      return (words[0][0] + words[1][0]).toUpperCase();
    },

    pillClassForAction(action) {
      if (action === 'approve') return 'pending';
      if (action === 'send') return 'ready';
      if (action === 'unpaid') return 'unpaid';
      return 'pending';
    },

    pillLabelForAction(action) {
      if (action === 'approve') return 'To approve';
      if (action === 'send') return 'To send';
      if (action === 'unpaid') return 'Unpaid';
      return '';
    },

    getPropertyOwner(property) {
      const report = this.monthEndReports.find(r => r.property_id === property.id || r.property_id === String(property.id));
      if (report && report.owner_name && report.owner_name !== 'N/A') {
        return report.owner_name;
      }
      return '';
    },

    async fetchDashboardData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
      if (!selectedPms || !selectedPms.id) {
        alert('No PMS configuration selected. Please select a PMS from the Hub.');
        this.$router.push('/');
        return;
      }

      const pmsConfigId = selectedPms.id;
      const headers = { 'Authorization': `Bearer ${token}` };
      this.loadingGlobalStats = true;
      this.loadingPropertyReservations = true;

      try {
        const res = await axios.get(`${this.baseUrl}/api/pms/dashboard/`, {
          headers,
          params: {
            pms_config_id: pmsConfigId,
            year: this.selectedYear,
            month: this.selectedMonth
          }
        });

        const payload = res.data || {};
        this.properties = payload.properties || [];
        this.propertiesData = payload.propertiesData || [];
        this.monthEndReports = payload.monthEndReports || [];
        this.monthEndReportsStats = payload.monthEndReportsStats || {
          total: 0,
          draft: 0,
          sent: 0,
          ready_to_send: 0,
          pushed_to_xero: 0,
          complete: 0,
          total_portfolio: 0
        };
        this.outstandingPayments = payload.outstandingPayments || {
          amount: 0,
          count_unpaid: 0,
          total_billed: 0
        };
        this.needsAttention = payload.needsAttention || [];

        if (this.properties.length > 0 && !this.selectedPropertyForReservations) {
          this.selectedPropertyForReservations = this.properties[0].id;
        }

        this.calculateGlobalStats();
        this.fetchSelectedPropertyReservations();
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } finally {
        this.loadingGlobalStats = false;
        this.loadingPropertyReservations = false;
      }
    },

    calculateGlobalStats() {

      const totalArrivingToday = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCount || 0), 0) || 0;
      const totalArrivingTodayCost = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCost || 0), 0) || 0;
      const totalActiveReservations = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCount || 0), 0) || 0;
      const totalActiveReservationsCost = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCost || 0), 0) || 0;
      const totalMonthRevenue = this.propertiesData?.reduce((total, prop) => total + (prop.monthRevenue || 0), 0) || 0;
      const totalBookings = this.propertiesData?.reduce((total, prop) => total + (prop.monthBookings || 0), 0) || 0;
      const totalManagementCommission = this.propertiesData?.reduce((total, prop) => total + (prop.managementCommission || 0), 0) || 0;
      const totalManagementCommissionExVAT = this.propertiesData?.reduce((total, prop) => total + (prop.managementCommissionExVAT || 0), 0) || 0;
      const totalVAT = this.propertiesData?.reduce((total, prop) => total + (prop.vatAmount || 0), 0) || 0;
      const totalOwnerNetEarnings = this.propertiesData?.reduce((total, prop) => total + (prop.ownerNetEarnings || 0), 0) || 0;

      // Build global reservations lists (all properties combined)
      this.globalArrivingToday = [];
      this.globalActiveReservations = [];

      this.propertiesData?.forEach(prop => {
        // Add property name to each reservation for display
        if (prop.arrivingToday && Array.isArray(prop.arrivingToday)) {
          prop.arrivingToday.forEach(res => {
            this.globalArrivingToday.push({
              ...res,
              propertyName: prop.name
            });
          });
        }

        if (prop.activeReservations && Array.isArray(prop.activeReservations)) {
          prop.activeReservations.forEach(res => {
            this.globalActiveReservations.push({
              ...res,
              propertyName: prop.name
            });
          });
        }
      });

      // Ensure globalStats always has proper structure with default values
      this.globalStats = {
        totalProperties: this.properties?.length || 0,
        totalArrivingToday: totalArrivingToday,
        totalArrivingTodayCost: totalArrivingTodayCost,
        totalActiveReservations: totalActiveReservations,
        totalActiveReservationsCost: totalActiveReservationsCost,
        totalMonthRevenue: totalMonthRevenue,
        totalBookings: totalBookings,
        totalManagementCommission: totalManagementCommission,
        totalManagementCommissionExVAT: totalManagementCommissionExVAT,
        totalVAT: totalVAT,
        totalOwnerNetEarnings: totalOwnerNetEarnings
      };

    },

    async fetchSelectedPropertyReservations() {
      if (!this.selectedPropertyForReservations) return;

      const propertyData = this.propertiesData.find(p => p.id == this.selectedPropertyForReservations);
      if (propertyData) {
        this.selectedPropertyData = {
          arrivingToday: propertyData.arrivingToday,
          arrivingTodayCount: propertyData.arrivingTodayCount,
          activeReservations: propertyData.activeReservations,
          activeReservationsCount: propertyData.activeReservationsCount
        };
      }
    },

    initializeDefaults() {
      // Initialize all data structures to prevent undefined errors
      this.globalStats = {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0
      };

      this.selectedPropertyData = {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      };

      // Initialize loading states
      this.loadingGlobalStats = true;
      this.loadingPropertyReservations = true;
    }
  },

  created() {
    // Ensure globalStats is properly initialized even before API calls
    this.initializeDefaults();
  },

  mounted() {
    this.loadSelectedMonth();
    this.fetchDashboardData();
  }
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --sidebar:     #5b58d4;
  --sidebar-lt:  #6b4fd8;
  --purple:      #8075f0;
  --purple-lt:   #a99ff6;
  --purple-dim:  rgba(128,117,240,0.1);
  --purple-border: rgba(128,117,240,0.22);
  --navy:        #0f0e2e;
  --bg:          #f5f3ff;
  --white:       #ffffff;
  --text:        #12113a;
  --text-mid:    #4a4870;
  --text-muted:  #9896b8;
  --border:      #ebe9f8;
  --green:       #00b894;
  --green-dim:   rgba(0,184,148,0.1);
  --amber:       #f59e0b;
  --amber-dim:   rgba(245,158,11,0.1);
  --gold:        #c8a84b;
}

* {
  font-family: 'DM Sans', sans-serif;
}

h1, h2, h3, .value, .commission-value, .pct, .count-val, .prop-commission {
  font-family: 'Bricolage Grotesque', sans-serif;
}

.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f5f3ff !important;
}

/* ── SIDEBAR ── */
/* Sidebar utilise les styles du composant Sidebar.vue */

/* ── MAIN ── */
.main {
  margin-left: 240px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  padding: 28px 36px;
  flex: 1;
}

/* ── LOADING STATE ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  gap: 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #5b58d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-state p {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ── STAT CARDS ── */
.stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  animation: fadeIn 0.4s 0.05s both;
}

.stat-card {
  background: #ffffff !important;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  transition: box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.stat-card .info {
  flex: 1;
}

.stat-card .label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.stat-card .value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.9rem;
  font-weight: 800;
  color: var(--text);
  line-height: 1;
}

.stat-card .sub {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 5px;
}

.mini-progress-bar {
  height: 5px;
  background: #ebe9f8;
  border-radius: 99px;
  overflow: hidden;
  margin-top: 8px;
}

.mini-progress-fill {
  height: 100%;
  background: #5b58d4;
  border-radius: 99px;
  transition: width 0.6s ease;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.stat-icon.purple {
  background: var(--purple-dim);
  color: var(--purple);
}

.stat-icon.green {
  background: var(--green-dim);
  color: var(--green);
}

.stat-icon.amber {
  background: var(--amber-dim);
  color: var(--amber);
}

.stat-icon svg {
  width: 20px;
  height: 20px;
  display: block;
}

/* ── COMMISSION BANNER ── */
.commission-banner {
  background: linear-gradient(135deg, #3430a0 0%, #4a46b8 100%);
  border-radius: 14px;
  padding: 24px 28px;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.4s 0.12s both;
}

.commission-banner::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
  pointer-events: none;
}

.commission-banner::after {
  content: '';
  position: absolute;
  bottom: -40px;
  left: 30%;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
  pointer-events: none;
}

.commission-main {
  position: relative;
  z-index: 1;
}

.commission-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.5);
  margin-bottom: 6px;
}

.commission-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 2.6rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.commission-sub {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  margin-top: 6px;
}

.commission-breakdown {
  display: flex;
  gap: 28px;
  position: relative;
  z-index: 1;
}

.breakdown-item {
  text-align: right;
}

.breakdown-item .b-label {
  font-size: 0.62rem;
  color: rgba(255,255,255,0.4);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.breakdown-item .b-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
}

.calculate-btn-inline {
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'DM Sans', sans-serif;
}

.calculate-btn-inline:hover {
  background: rgba(255,255,255,0.3);
}

.mini-spinner {
  border: 2px solid rgba(255,255,255,0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

/* ── BOTTOM GRID ── */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  animation: fadeIn 0.4s 0.2s both;
}

/* ── CARDS ── */
.card {
  background: #ffffff !important;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);
}

.card-action {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--purple);
  text-decoration: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid var(--purple-border);
  background: #f7f6ff;
  transition: background 0.15s;
}

.card-action:hover {
  background: var(--purple-dim);
}

.card-body {
  padding: 18px 20px;
}

/* progress ring */
.reports-summary {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 18px;
}

.progress-ring-wrap {
  position: relative;
  flex-shrink: 0;
}

.progress-ring-wrap svg {
  transform: rotate(-90deg);
}

.ring-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.pct {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--text);
}

.pct-lbl {
  font-size: 0.55rem;
  color: var(--text-muted);
}

.reports-counts {
  flex: 1;
}

.count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 0.82rem;
  border-bottom: 1px solid var(--border);
}

.count-row:last-child {
  border: none;
}

.count-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  display: inline-block;
}

.count-lbl {
  color: var(--text-mid);
  display: flex;
  align-items: center;
}

.count-val {
  font-weight: 700;
  color: var(--text);
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.95rem;
}

/* report rows */
.report-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.report-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
  gap: 12px;
}

.report-row:last-child {
  border: none;
}

.report-row .prop {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
}

.report-row .owner {
  font-size: 0.72rem;
  color: var(--text-muted);
}

.report-row .fee {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  min-width: 60px;
  text-align: right;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 0.62rem;
  font-weight: 700;
}

.status-pill.pending {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}

.status-pill.draft {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.status-pill.ready {
  background: #fef3c7;
  color: #f59e0b;
  border: 1px solid #fcd34d;
}

.status-pill.sent {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-pill.pushed {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #93c5fd;
}

.status-pill.unpaid {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

/* ── PROPERTIES CARD ── */
.prop-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.prop-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid var(--border);
}

.prop-row:last-child {
  border: none;
}

.prop-initial {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f2f1fd;
  border: 1px solid var(--purple-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--purple);
  flex-shrink: 0;
}

.prop-info {
  flex: 1;
}

.prop-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
}

.prop-owner {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.prop-commission {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
}

.prop-commission-lbl {
  font-size: 0.62rem;
  color: var(--text-muted);
  text-align: right;
}

/* ── GLOBAL MONTH PICKER ── */
.picker-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.global-month-picker {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 4px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.month-nav-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--text-mid);
  cursor: pointer;
  border-radius: 5px;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}

.month-nav-btn:hover {
  background: var(--purple-dim);
  color: var(--purple);
}

.month-nav-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
  min-width: 108px;
  text-align: center;
  padding: 0 8px;
  font-family: 'DM Sans', sans-serif;
}

/* ── MONTH TABS ── */
.month-tabs {
  display: flex;
  gap: 6px;
}

.month-tab {
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--border);
  color: var(--text-muted);
  background: var(--bg);
  transition: all 0.15s;
}

.month-tab.active {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
  color: #fff;
  border-color: var(--sidebar);
}

.month-tab:hover:not(.active) {
  border-color: var(--purple-border);
  color: var(--purple);
}

/* ── EMPTY STATE ── */
.empty-state-small {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-muted);
}

.empty-state-small p {
  font-size: 0.82rem;
  margin: 0;
}

/* ── ANIMATIONS ── */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── RESPONSIVE ── */
@media (max-width: 1200px) {
  .bottom-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1024px) {
  .content {
    padding: 22px 20px;
  }
}

@media (max-width: 768px) {
  .content {
    padding: 18px 14px;
  }

  .stats-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-card {
    padding: 16px 18px;
  }

  .stat-card .value {
    font-size: 1.7rem;
  }

  .commission-banner {
    grid-template-columns: 1fr;
    padding: 20px 22px;
  }

  .commission-value {
    font-size: 2.1rem;
  }

  .commission-breakdown {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 18px;
    align-items: flex-start;
  }

  .breakdown-item {
    text-align: left;
  }

  .card-header {
    padding: 14px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-body {
    padding: 14px 16px;
  }

  .picker-row {
    justify-content: center;
  }

  .month-nav-label {
    min-width: 132px;
  }
}

@media (max-width: 480px) {
  .content {
    padding: 14px 10px;
  }

  .stat-card {
    padding: 14px 16px;
  }

  .commission-banner {
    padding: 18px 18px;
  }

  .commission-value {
    font-size: 1.8rem;
  }

  .commission-breakdown {
    gap: 14px;
  }

  .report-row .fee {
    min-width: 50px;
    font-size: 0.78rem;
  }

  .status-pill {
    font-size: 0.58rem;
    padding: 3px 8px;
  }
}
</style>
